"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import React, { useMemo, useRef } from "react";
import KeyNumbersCard from "../../components/molecules/KeyNumbersCard";

interface MetaItem {
  id: string | number;
  meta_title: string;
  meta_subtitle: string;
  meta_priority: number;
  meta_title_text_color?: string;
  meta_subtitle_text_color?: string;
  meta_background_color?: string;
  meta_status?: boolean;
}

const KeyNumbers1 = ({ data }: { data: any }) => {
  const { data: sectionData } = data;
  const ref = useRef<HTMLDivElement>(null);

  const { metadata = [], background_color = "#BE2330" } = sectionData || {};

  // Hooks stay above every early return — bailing out on `status` first made the
  // hook count vary between renders (React: "rendered fewer hooks than expected").
  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: MetaItem) => item?.meta_status == true)
      .sort((a: MetaItem, b: MetaItem) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  if (!sectionData?.status) return null;
  if (!sortedMetadata?.length || !data) return null;

  const metadataCount = sortedMetadata.length;
  const isTwoItems = metadataCount === 2;
  const isFourItems = metadataCount === 4;
  // Any other count (1, 3, 5+). These used to fall through with NO layout class
  // at all, so the cards stayed block-level and stacked one under another — on
  // desktop too. They get the same single-row treatment as the 2-item case.
  const isOtherCount = !isTwoItems && !isFourItems;

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "key_numbers_section");
  //   }
  // }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "w-full",
        "xs:py-8 xs:px-5",
        "sm:py-spacing-24 sm:px-spacing-32 xl:px-spacing-32 xl:py-spacing-32",
        // For 2 items: horizontal layout on both mobile and desktop
        isTwoItems && "flex items-center justify-around",
        // For 4 items: 2x2 grid on mobile, horizontal on desktop
        isFourItems &&
          "grid grid-cols-2 grid-rows-2 sm:flex sm:items-center sm:justify-around",
        // Any other count: stacked on mobile (too little width for 3+ side by
        // side), single centered row from sm up. flex-wrap keeps 5+ items from
        // overflowing the section on narrower desktops.
        isOtherCount &&
          "flex flex-col items-center gap-8 sm:flex-row sm:flex-wrap sm:items-center sm:justify-around sm:gap-0",
      )}
      style={{ backgroundColor: background_color }}
    >
      {sortedMetadata.map((keyNumber: MetaItem, index: number) => (
        <React.Fragment key={keyNumber.id || index}>
          <KeyNumbersCard
            keyNumber={keyNumber}
            index={index}
            totalCount={metadataCount}
          />

          {/* Add vertical divider between items (not after last item) */}
          {index < sortedMetadata.length - 1 && (
            <div
              className={cn(
                "bg-white/30",
                // For 2 items: show divider on all screens
                isTwoItems && "w-[1px] h-12 sm:h-16",
                // For 4 items: show divider only on desktop
                isFourItems && "hidden sm:block w-[1px] h-16",
                // Any other count: desktop only — on mobile the cards stack, so
                // a vertical rule between them would sit sideways.
                isOtherCount && "hidden sm:block w-[1px] h-16",
              )}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default KeyNumbers1;
